import { useState } from 'react'
import { IconeChevronBaixo, IconeSelecao } from '../icones'
import type { GrupoFerramentas } from '../ribbon/BarraFerramentasCenario'
import type { ConteudoInspetor } from '../modulos/tipos'
import { FolhaInferior } from './FolhaInferior'
import type { Ferramenta } from './TrilhaFerramentas'

interface Props {
  grupos: readonly GrupoFerramentas[]
  grupoAtivo: string | null
  aoMudarGrupo: (id: string | null) => void
  /** Rótulo do módulo, para nomear a folha. */
  tituloModulo: string
  /** Ações principais do módulo, sempre à vista sobre o canvas. */
  acoesRapidas?: React.ReactNode
  /** Propriedades do que está selecionado. Abre em folha, sob demanda. */
  inspetor?: ConteudoInspetor | null
  /**
   * Ferramentas de colocação do módulo (calço, cunha, V-strut…). No desktop
   * ficam na trilha lateral; aqui viram uma faixa rolável logo acima do dock,
   * ao alcance do polegar e sem abrir folha nenhuma.
   */
  ferramentas?: readonly Ferramenta[]
}

/**
 * Dock inferior do celular.
 *
 * No celular não há trilha lateral nem inspetor fixo: eles comeriam a tela que o
 * 3D precisa. Os grupos do módulo viram uma faixa de toque no rodapé, e o
 * conteúdo abre numa folha inferior que cobre pouco mais da metade da altura —
 * o suficiente para ajustar sem perder de vista o que está sendo ajustado.
 *
 * Os grupos são os MESMOS do desktop, vindos do mesmo manifesto. Nada é
 * escondido no celular: a estrutura é que muda.
 */
/** Acima disto, os grupos dividem a largura apertados e usam o rótulo curto. */
const MAXIMO_GRUPOS_ROTULO_COMPLETO = 5

export function DockMobile({
  grupos: todosGrupos,
  grupoAtivo,
  aoMudarGrupo,
  tituloModulo,
  acoesRapidas,
  inspetor,
  ferramentas,
}: Props) {
  const comFerramentas = ferramentas != null && ferramentas.length > 0
  const grupos = todosGrupos.filter((grupo) => !grupo.somenteDesktop)
  const rotulosCurtos = grupos.length > MAXIMO_GRUPOS_ROTULO_COMPLETO

  const aberto = grupos.find((grupo) => grupo.id === grupoAtivo) ?? null
  const [inspetorAberto, setInspetorAberto] = useState(false)

  return (
    <>
      {/* A seleção não abre a folha sozinha no celular: ela taparia o objeto que
         o aluno acabou de tocar. Fica uma barra fina anunciando o quê, e o
         detalhe abre com um toque. */}
      {inspetor && (
        <div className="dock-mobile__selecao-wrapper">
          <button
            type="button"
            className="dock-mobile__selecao"
            onClick={() => setInspetorAberto(true)}
            aria-expanded={inspetorAberto}
          >
            <IconeSelecao tamanho={14} />
            <span className="dock-mobile__selecao-nome">{inspetor.titulo}</span>
            <IconeChevronBaixo tamanho={14} />
          </button>
          {inspetor.acoesRapidasMobile && (
            <div className="dock-mobile__selecao-acoes" role="group" aria-label="Ações rápidas da seleção">
              {inspetor.acoesRapidasMobile}
            </div>
          )}
        </div>
      )}

      {inspetor && (
        <FolhaInferior
          aberta={inspetorAberto}
          titulo={inspetor.titulo}
          subtitulo={inspetor.subtitulo}
          aoFechar={() => setInspetorAberto(false)}
          fracaoAltura={0.5}
        >
          {inspetor.propriedades && inspetor.propriedades.length > 0 && (
            <dl className="inspetor__propriedades">
              {inspetor.propriedades.map((propriedade) => (
                <div key={propriedade.rotulo} className="inspetor__propriedade">
                  <dt>{propriedade.rotulo}</dt>
                  <dd>{propriedade.valor}</dd>
                </div>
              ))}
            </dl>
          )}
          {inspetor.acoes && <div className="inspetor__acoes">{inspetor.acoes}</div>}
        </FolhaInferior>
      )}

      {comFerramentas && (
        <div className="dock-mobile__ferramentas" role="toolbar" aria-label={`Equipamentos de ${tituloModulo}`}>
          {ferramentas.map((ferramenta) => (
            <button
              key={ferramenta.id}
              type="button"
              className={`dock-mobile__ferramenta${ferramenta.ativa ? ' ativa' : ''}`}
              onClick={ferramenta.aoAcionar}
              aria-pressed={ferramenta.ativa ?? false}
              disabled={ferramenta.motivoIndisponivel != null}
              title={ferramenta.motivoIndisponivel ?? ferramenta.rotulo}
            >
              <ferramenta.Icone tamanho={18} />
              <span>{ferramenta.rotulo}</span>
            </button>
          ))}
        </div>
      )}

      {acoesRapidas && (
        <div
          className={`dock-mobile__acoes${comFerramentas ? ' dock-mobile__acoes--sobre-ferramentas' : ''}`}
          aria-label="Ações rápidas"
        >
          {acoesRapidas}
        </div>
      )}

      <nav className="dock-mobile" aria-label={`Ferramentas de ${tituloModulo}`}>
        {grupos.map((grupo) => {
          const ativo = grupo.id === aberto?.id
          // A etapa do roteiro em que o aluno está fica acesa mesmo com a folha
          // fechada: sem isso o dock não dizia onde ele estava.
          const aceso = ativo || grupo.ativa === true
          return (
            <button
              key={grupo.id}
              type="button"
              className={`dock-mobile__item${aceso ? ' ativo' : ''}`}
              onClick={() => {
                if (grupo.aoAtivar) {
                  grupo.aoAtivar()
                  aoMudarGrupo(grupo.id)
                } else aoMudarGrupo(ativo ? null : grupo.id)
              }}
              disabled={grupo.desabilitado}
              aria-expanded={ativo}
              aria-label={grupo.rotulo}
            >
              {grupo.icone}
              <span className="dock-mobile__rotulo">{rotulosCurtos ? grupo.rotuloCurto ?? grupo.rotulo : grupo.rotulo}</span>
              {grupo.badge != null && <span className="dock-mobile__badge">{grupo.badge}</span>}
            </button>
          )
        })}
      </nav>

      <FolhaInferior
        aberta={aberto != null}
        titulo={aberto?.rotulo ?? ''}
        subtitulo={aberto?.resumo}
        aoFechar={() => aoMudarGrupo(null)}
      >
        {aberto?.conteudo()}
      </FolhaInferior>
    </>
  )
}
